export const HOMEPAGE_SECTION_TYPES = [
  'hero',
  'aboutBio',
  'services',
  'ventures',
  'testimonials',
  'faq',
  'cta',
];

export const ABOUT_SECTION_TYPES = ['hero', 'tabs', 'statsRow', 'quote', 'cta'];

export const PAGE_SECTION_TYPES = [
  'hero',
  'textBlock',
  'overview',
  'cards',
  'videoEmbed',
  'testimonials',
  'cta',
  'statsRow',
  'imageGallery',
  'faq',
  'ventures',
  'inquiryPaths',
  'contactForm',
  'contactInfo',
  'joinForm',
  'benefits',
  'pageChrome',
];

export const sortSections = (sections = []) =>
  [...sections].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

export const enabledSections = (sections = []) =>
  sortSections(sections).filter((section) => section.enabled !== false);

export const renumberSortOrder = (sections = []) =>
  sortSections(sections).map((section, index) => ({ ...section, sortOrder: index }));

export const findSectionContent = (sections = [], type) => {
  const match = enabledSections(sections).find((section) => section.type === type);
  return match?.content && typeof match.content === 'object' ? match.content : {};
};

const findSectionByIdOrType = (sections = [], id, type, typeIndex = 0) => {
  const list = enabledSections(sections);
  const byId = list.find((section) => section.id === id || String(section.id || '').startsWith(`${id}-`));
  if (byId?.content && typeof byId.content === 'object') return byId.content;
  const matches = list.filter((section) => section.type === type);
  const match = matches[typeIndex];
  return match?.content && typeof match.content === 'object' ? match.content : {};
};

const asButton = (value, fallback = {}) => {
  const button = value && typeof value === 'object' ? value : {};
  return {
    text: button.text || button.label || fallback.text || '',
    link: button.link || button.href || fallback.link || '',
  };
};

export const landingBagFromCmsSections = (sections = []) => {
  const hero = findSectionByIdOrType(sections, 'hero', 'hero', 0);
  const projectGoal = findSectionByIdOrType(sections, 'project-goal', 'textBlock', 0);
  const valueProps = findSectionByIdOrType(sections, 'value-props', 'cards', 0);
  const purchase = findSectionByIdOrType(sections, 'purchase-options', 'cards', 1);
  const sponsor = findSectionByIdOrType(sections, 'sponsor', 'cta', 0);
  const reel = findSectionByIdOrType(sections, 'reel', 'videoEmbed', 0);
  const testimonials = findSectionByIdOrType(sections, 'testimonials', 'testimonials', 0);
  const finalCta = findSectionByIdOrType(sections, 'final-cta', 'cta', 1);

  const purchaseCards = Array.isArray(purchase.cards) ? purchase.cards : [];
  const hardcopy = purchaseCards.length
    ? purchaseCards.find((card) => card?.kind === 'hardcopy') || purchaseCards[0]
    : purchase.hardcopy && typeof purchase.hardcopy === 'object' && Object.keys(purchase.hardcopy).length
      ? purchase.hardcopy
      : null;
  const softcopy = purchaseCards.length
    ? purchaseCards.find((card) => card?.kind === 'softcopy') || (purchaseCards[1] && purchaseCards[1] !== hardcopy ? purchaseCards[1] : null)
    : purchase.softcopy && typeof purchase.softcopy === 'object' && Object.keys(purchase.softcopy).length
      ? purchase.softcopy
      : null;

  const heroTitleLines = Array.isArray(hero.titleLines) && hero.titleLines.length
    ? hero.titleLines
    : [hero.title || hero.headline].filter(Boolean);
  const reelVideos = Array.isArray(reel.videos) && reel.videos.length
    ? reel.videos
    : reel.videoUrl || reel.videoSrc
      ? [{
          title: reel.title || reel.headline,
          description: reel.description,
          url: reel.url,
          buttonText: reel.buttonText,
          videoSrc: reel.videoSrc || reel.videoUrl,
        }]
      : [];

  return {
    hero: {
      ...hero,
      titleLines: heroTitleLines,
      title: hero.title || hero.headline || heroTitleLines[0] || '',
      description: hero.description || hero.body || '',
      primaryCta: asButton(hero.primaryCta, { text: hero.ctaLabel || hero.ctaText, link: hero.ctaHref || hero.ctaLink }),
      secondaryCta: asButton(hero.secondaryCta),
      bookImage: hero.bookImage || hero.imageUrl || hero.image || '',
      priceNote: hero.priceNote || '',
    },
    projectGoal: {
      ...projectGoal,
      label: projectGoal.label || '',
      title: projectGoal.title || projectGoal.headline || '',
      description: projectGoal.description || projectGoal.body || '',
      impactNote: projectGoal.impactNote || '',
      stats: Array.isArray(projectGoal.stats) ? projectGoal.stats : [],
    },
    valueProps: {
      label: valueProps.label || '',
      title: valueProps.title || valueProps.headline || '',
      items: Array.isArray(valueProps.items)
        ? valueProps.items
        : Array.isArray(valueProps.cards)
          ? valueProps.cards
          : [],
    },
    purchaseOptions: {
      label: purchase.label || '',
      title: purchase.title || purchase.headline || '',
      hardcopy: hardcopy || undefined,
      softcopy: softcopy || undefined,
    },
    sponsorCopy: {
      ...sponsor,
      label: sponsor.label || '',
      title: sponsor.title || sponsor.headline || '',
      description: sponsor.description || sponsor.body || '',
      buttonText: sponsor.buttonText || sponsor.ctaText || sponsor.ctaLabel || '',
      link: sponsor.link || sponsor.ctaLink || sponsor.ctaHref || '',
    },
    reel: {
      ...reel,
      label: reel.label || '',
      title: reel.title || reel.headline || '',
      description: reel.description || '',
      videos: reelVideos,
    },
    testimonials: {
      label: testimonials.label || '',
      title: testimonials.title || testimonials.headline || '',
      description: testimonials.description || '',
      items: Array.isArray(testimonials.items) ? testimonials.items : [],
    },
    finalCta: {
      ...finalCta,
      title: finalCta.title || finalCta.headline || '',
      description: finalCta.description || finalCta.body || '',
      primaryButton: asButton(finalCta.primaryButton, { text: finalCta.ctaText || finalCta.ctaLabel, link: finalCta.ctaLink || finalCta.ctaHref }),
      secondaryButton: asButton(finalCta.secondaryButton),
    },
  };
};

export const looksLikeBookLanding = (bag = {}) =>
  Boolean(
    bag.hero?.bookImage ||
    (Array.isArray(bag.hero?.titleLines) && bag.hero.titleLines.length) ||
    bag.purchaseOptions?.hardcopy ||
    bag.projectGoal?.title,
  );

/** Rebuild legacy-shaped page bags from CMS sections for existing React pages. */
export const bagFromCmsSections = (slug, sections = []) => {
  const hero = findSectionContent(sections, 'hero');
  const chrome = findSectionContent(sections, 'pageChrome');
  const normalizeHero = {
    label: hero.label || '',
    title: hero.title || hero.headline || '',
    description: hero.description || hero.body || hero.subheadline || '',
    image: hero.image || hero.imageUrl || '',
    primaryButton: hero.primaryButton || {
      text: hero.ctaText || hero.ctaLabel || '',
      link: hero.ctaLink || hero.ctaHref || '',
    },
  };

  switch (slug) {
    case 'community': {
      const overview = findSectionContent(sections, 'overview');
      const gallery = findSectionContent(sections, 'imageGallery');
      const benefits = findSectionContent(sections, 'benefits');
      const testimonials = findSectionContent(sections, 'testimonials');
      const cta = findSectionContent(sections, 'cta');
      const joinForm = findSectionContent(sections, 'joinForm');
      return {
        hero: normalizeHero,
        overview: {
          title: overview.headline || overview.title || '',
          description: overview.body || overview.description || '',
        },
        gallery: {
          title: gallery.headline || gallery.title || '',
          description: gallery.description || '',
          images: Array.isArray(gallery.galleryUrls)
            ? gallery.galleryUrls.map((url) => (typeof url === 'string' ? { imageUrl: url } : url))
            : Array.isArray(gallery.images)
              ? gallery.images
              : [],
        },
        benefits: Array.isArray(benefits.items) ? benefits.items : Array.isArray(benefits.benefits) ? benefits.benefits : [],
        testimonials: Array.isArray(testimonials.items)
          ? testimonials.items
          : Array.isArray(testimonials.testimonials)
            ? testimonials.testimonials
            : [],
        cta: {
          title: cta.title || cta.headline || '',
          description: cta.description || cta.body || '',
          primaryButton: cta.primaryButton || {
            text: cta.ctaText || cta.ctaLabel || '',
            link: cta.ctaLink || cta.ctaHref || '',
          },
        },
        joinForm: {
          ...joinForm,
          submitButton: joinForm.submitButton || joinForm.submitLabel || joinForm.buttonText || 'Submit Application',
          submitLabel: joinForm.submitLabel || joinForm.submitButton || joinForm.buttonText || 'Submit Application',
        },
      };
    }
    case 'contact': {
      const inquiry = findSectionContent(sections, 'inquiryPaths');
      const form = findSectionContent(sections, 'contactForm');
      const info = findSectionContent(sections, 'contactInfo');
      const faq = findSectionContent(sections, 'faq');
      return {
        contactContent: {
          hero: normalizeHero,
          inquiryPaths: Array.isArray(inquiry.items) ? inquiry.items : Array.isArray(inquiry.inquiryPaths) ? inquiry.inquiryPaths : [],
          form: {
            ...form,
            submitButton: form.submitButton || form.submitLabel || form.buttonText || 'Send Inquiry',
            submitLabel: form.submitLabel || form.submitButton || form.buttonText || 'Send Inquiry',
          },
          contactInfo: info,
        },
        faqContent: faq,
      };
    }
    case 'shop':
      return { hero: normalizeHero };
    case 'blog':
      return {
        title: chrome.title || '',
        description: chrome.description || '',
        featuredLabel: chrome.featuredLabel || 'Featured Story',
        allPostsLabel: chrome.allPostsLabel || 'All posts',
        emptyStateTitle: chrome.emptyStateTitle || '',
        emptyStateDescription: chrome.emptyStateDescription || '',
        posts: [],
      };
    case 'resources':
      return {
        hero: normalizeHero,
        sections: {
          books: { title: chrome.booksTitle || '', books: [] },
          newsletter: {
            title: chrome.newsletterTitle || '',
            description: chrome.newsletterDescription || '',
          },
          waitlist: chrome.waitlistTitle || chrome.waitlistDescription || chrome.waitlistVideo
            ? {
                title: chrome.waitlistTitle || '',
                description: chrome.waitlistDescription || '',
                video: chrome.waitlistVideo || '',
              }
            : null,
        },
      };
    case 'press-kit':
      return {
        hero: normalizeHero,
        sections: {
          headshots: {
            title: chrome.headshotsTitle || '',
            description: chrome.headshotsDescription || '',
            images: [],
          },
          bios: {
            title: chrome.biosTitle || '',
            description: chrome.biosDescription || '',
            items: [],
          },
        },
      };
    case 'ventures':
      return findSectionContent(sections, 'ventures');
    case 'project-2000':
      return landingBagFromCmsSections(sections);
    default:
      return landingBagFromCmsSections(sections);
  }
};
